import type { ReactNode } from 'react'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { isHtmlAsset, isVideoAsset } from '@/services/assets/assetsApi'
import { DEFAULT_HTML_SIZE, ScaledHtmlFrame } from './HtmlPreviewThumbnail'
import { Button } from '@/components/ui/button'
import { Download } from 'lucide-react'
import { formatBytes, formatNumber } from '../utils/format'

interface AssetHoverPreviewProps {
  /** CDN URL of the asset to show enlarged. */
  src?: string | null
  mimeType?: string | null
  /** Shown under the asset, e.g. the file name. */
  caption?: string
  /** File size in bytes, shown beside the dimensions. */
  fileSize?: number | null
  width?: number | null
  height?: number | null
  /** Offers a download of the underlying file from inside the preview. */
  onDownload?: () => void
  downloadLabel?: string
  children: ReactNode
}

/**
 * Enlarges an image, video or HTML asset on hover. Thumbnails in lists and
 * pickers are too small to judge a creative by, and opening the asset just to
 * look at it is a detour. Anything else renders its trigger alone.
 */
export function AssetHoverPreview({
  src,
  mimeType,
  caption,
  fileSize,
  width,
  height,
  onDownload,
  downloadLabel,
  children,
}: AssetHoverPreviewProps) {
  const isVideo = isVideoAsset(mimeType ?? '')
  const isImage = !isVideo && !!mimeType && /^(image|IMAGE)/.test(mimeType)
  const isHtml = isHtmlAsset(mimeType ?? '')

  if (!src || (!isVideo && !isImage && !isHtml)) return <>{children}</>

  const details = [
    width && height ? `${formatNumber(width)}×${formatNumber(height)}` : null,
    fileSize ? formatBytes(fileSize) : null,
  ].filter(Boolean)

  return (
    <HoverCard openDelay={250} closeDelay={80}>
      {/* Radix renders the trigger as an anchor by default, which cannot legally
          wrap the buttons used here — so hand it a span of our own instead. */}
      <HoverCardTrigger asChild>
        <span className="inline-block leading-none">{children}</span>
      </HoverCardTrigger>
      <HoverCardContent side="right" className="w-auto max-w-[min(1040px,90vw)] p-2">
        <div className="flex flex-col gap-2">
          {isVideo ? (
            <video
              src={src}
              className="block max-h-[80vh] max-w-full rounded"
              autoPlay
              muted
              loop
              playsInline
            />
          ) : isHtml ? (
            <ScaledHtmlFrame
              src={src}
              width={width || DEFAULT_HTML_SIZE.width}
              height={height || DEFAULT_HTML_SIZE.height}
              maxWidth={640}
              maxHeight={640}
            />
          ) : (
            <img src={src} alt={caption ?? ''} className="block max-h-[80vh] max-w-full rounded" />
          )}
          {(caption || details.length > 0 || onDownload) && (
            <div className="flex items-center justify-between gap-3 text-xs text-(--ad-text-muted)">
              <span className="truncate">{caption}</span>
              <span className="flex shrink-0 items-center gap-2">
                {details.length > 0 && <span className="tabular-nums">{details.join(' · ')}</span>}
                {onDownload && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onDownload()
                    }}
                  >
                    <Download className="size-3.5" />
                    {downloadLabel}
                  </Button>
                )}
              </span>
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}
